/**
 * AccountMenu.jsx
 *
 * User account button with dropdown for signed-in users,
 * or prominent Sign In button for guests.
 *
 * Handles: Sign in, Sign out, Language preferences, Settings access
 */

import React, { useState, useRef, useEffect } from 'react';
import { useAuth, signOut } from './AuthView';
import { useQobuzStore } from '../lib/useQobuz';
import { ValetIcon } from './icons/ValetIcon';
import LANGUAGES from '../i18n/languages';

export default function AccountMenu({ onOpenSettings, onOpenAuth }) {
  const auth = useAuth();
  const user = auth.user;
  const [isOpen, setIsOpen] = useState(false);
  const [showLanguages, setShowLanguages] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    return localStorage.getItem('maestro_language') || 'en';
  });
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setIsOpen(false);
        setShowLanguages(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  const handleLanguageChange = (code) => {
    setCurrentLanguage(code);
    localStorage.setItem('maestro_language', code);
    setShowLanguages(false);
  };

  const handleSignOut = async () => {
    setIsOpen(false);
    try { await signOut(); } catch {}
    try { useQobuzStore.getState().disconnect(); } catch {}
    try { localStorage.removeItem('maestro_skip_auth'); } catch {}
    try { localStorage.removeItem('maestro-library-cache'); } catch (e) { console.warn('[AccountMenu] failed to clear library cache on sign-out:', e); }
    window.location.reload();
  };

  const currentLang = LANGUAGES.find(l => l.code === currentLanguage) || LANGUAGES[0];

  if (!user) {
    return (
      <div className="px-4 py-3">
        <button
          onClick={onOpenAuth}
          className="w-full flex items-center gap-3 p-2 rounded-xl transition group touch-target"
          style={{ minHeight: 44 }}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--sidebar-hover-bg)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
        >
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center"
            style={{ background: 'var(--sidebar-hover-bg)', color: 'var(--sidebar-text-sec)' }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          </div>
          <div className="flex-1 text-left min-w-0">
            <p className="text-sm font-medium" style={{ color: 'var(--sidebar-text-sec)' }}>Guest</p>
            <p className="text-xs" style={{ color: '#C9A84C' }}>Sign in →</p>
          </div>
        </button>
      </div>
    );
  }

  return (
    <div className="relative px-4 py-3" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-3 p-2 rounded-xl transition touch-target"
        style={{ minHeight: 44 }}
        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--sidebar-hover-bg)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
      >
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center text-black font-bold text-sm">
          {user.email?.[0]?.toUpperCase() || 'U'}
        </div>
        <div className="flex-1 text-left min-w-0">
          <p className="text-sm font-medium truncate" style={{ color: 'var(--sidebar-text)' }}>
            {user.user_metadata?.full_name || user.email?.split('@')[0] || 'User'}
          </p>
          <p className="text-xs truncate" style={{ color: 'var(--sidebar-text-sec)' }}>{user.email}</p>
        </div>
        <svg
          width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
          className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}
          style={{ color: 'var(--sidebar-text-sec)' }}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {isOpen && (
        <div
          className="absolute left-4 right-4 top-full mt-1 rounded-xl shadow-2xl py-1 z-50 overflow-hidden"
          style={{
            background: 'var(--bg-card, #2a2a2a)',
            border: '1px solid var(--border, rgba(255,255,255,0.1))',
          }}
        >
          <div className="relative">
            <button
              onClick={() => setShowLanguages(!showLanguages)}
              className="w-full px-4 py-2.5 text-left text-sm flex items-center justify-between touch-target"
              style={{ color: 'var(--text-primary)', minHeight: 44 }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--sidebar-hover-bg)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              <span className="flex items-center gap-2">
                <span>{currentLang.flag}</span>
                <span>Language</span>
              </span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>

            {showLanguages && (
              <div className="py-1 max-h-48 overflow-y-auto ios-scroll" style={{ borderTop: '1px solid var(--border-light)' }}>
                {LANGUAGES.map(lang => (
                  <button
                    key={lang.code}
                    onClick={() => handleLanguageChange(lang.code)}
                    className="w-full px-4 py-2 text-left text-sm flex items-center gap-2 touch-target"
                    style={{
                      color: currentLanguage === lang.code ? '#C9A84C' : 'var(--text-secondary)',
                      minHeight: 44,
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--sidebar-hover-bg)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <span>{lang.flag}</span>
                    <span>{lang.label}</span>
                    {currentLanguage === lang.code && (
                      <svg className="ml-auto" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button
            onClick={() => { setIsOpen(false); onOpenSettings?.(); }}
            className="w-full px-4 py-2.5 text-left text-sm flex items-center gap-2 touch-target"
            style={{ color: 'var(--text-primary)', minHeight: 44 }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--sidebar-hover-bg)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
            Settings
          </button>

          <div className="my-1" style={{ borderTop: '1px solid var(--border-light)' }} />

          <button
            onClick={handleSignOut}
            className="w-full px-4 py-2.5 text-left text-sm text-red-500 flex items-center gap-2 touch-target"
            style={{ minHeight: 44 }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(239,68,68,0.08)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            Sign Out
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * SignInPrompt - Friendly modal shown when guests try premium features
 */
export function SignInPrompt({ isOpen, onClose, onSignIn, feature }) {
  if (!isOpen) return null;

  const featureMessages = {
    play: {
      title: "Ready to Listen?",
      description: "Maestro is a paid service. Sign in to start listening and sync across your devices.",
      icon: "🎵"
    },
    valet: {
      title: "Meet Ella",
      description: "Sign in to get personalized recommendations and chat with Ella, your personal music valet.",
      icon: <ValetIcon size={48} />
    },
    playlist: {
      title: "Create Your Playlists",
      description: "Sign in to create playlists, save favorites, and sync across all your devices.",
      icon: "📝"
    },
    service: {
      title: "Connect Your Services",
      description: "Sign in to link Qobuz, Spotify and enjoy your music library in one place.",
      icon: "🔗"
    },
    default: {
      title: "Join Maestro",
      description: "Sign in to unlock the full audiophile experience.",
      icon: "✨"
    }
  };

  const msg = featureMessages[feature] || featureMessages.default;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div
        className="relative rounded-2xl p-8 max-w-sm w-full text-center shadow-2xl"
        style={{
          background: 'var(--bg-card, #1e1e30)',
          border: '1px solid var(--border, rgba(255,255,255,0.1))',
          color: 'var(--text-primary)',
        }}
      >
        <div className="text-5xl mb-4">{msg.icon}</div>
        <h2 className="text-2xl font-bold mb-2">{msg.title}</h2>
        <p className="mb-6" style={{ color: 'var(--text-secondary)' }}>{msg.description}</p>
        <button
          onClick={onSignIn}
          className="w-full py-3 px-6 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-black font-bold rounded-xl transition-all shadow-lg hover:shadow-xl touch-target"
        >
          Sign In
        </button>
        <button
          onClick={onClose}
          className="mt-4 text-sm transition touch-target"
          style={{ color: 'var(--text-tertiary)' }}
        >
          Maybe later
        </button>
      </div>
    </div>
  );
}
